import { storeToRefs } from 'pinia';
import { computed, ref } from '@nuxtjs/composition-api';
import { PRODUCT_TYPE } from '@/constants/ProductType';
import { CUSTOMER_SUBSCRIPTION_TYPE } from '~/constants/CheckoutFlow';
import { PURCHASE_CATEGORY } from '~/constants/PurchaseCategory';
import { VALIDATION_STATUS } from '~/constants/ValidationStatus';
import { BUNDLE_TYPE } from '@/constants/BundleType';
import { useCartStore } from '~/stores/useCartStore';
import useCartScenarios from '~/composable/useCartScenarios';
import useProductType from '~/composable/useProductType';
import usePersistedState from '~/composable/payment/usePersistedState';
import useCategories from '~/composable/useCategories';
// import usePlaymix from '~/composable/usePlaymix';
import useErrorHandler from '~/composable/useErrorHandler';
import { calculateInstallmentAmount as baseCalculateInstallmentAmount } from '~/composable/installmentMath';
import { voucherDebug, voucherDebugWarn } from '~/composable/voucher/useVoucherDebug';
import { useResolveVoucherDevicePrice } from '~/composable/voucher/useResolveVoucherDevicePrice';
import { patchDeviceLineItemPricesInCart } from '~/composable/voucher/resolveDevicePriceForCart';

const initializeCart = (cart, storeCart) => {
  if (!cart.value || Object.keys(cart.value).length < 1) {
    cart.value = storeCart.value || {}
  }
}

const getCurrentCart = (storeCart, localCart) => {
  return storeCart.value && Object.keys(storeCart.value).length > 0 ? storeCart.value : localCart.value
}

const getAllCartItems = (cartData) => {
  if (!cartData?.cartItemGroupList) {
    return []
  }
  return cartData.cartItemGroupList.flatMap(group => group.cartItems || [])
}

const cartContainsDeviceSku = (cartData, sku) => {
  if (!sku || !cartData) {
    return false
  }
  return getAllCartItems(cartData).some(item =>
    item.cartProduct?.sku === sku &&
    (item.cartProduct?.productTypeKey === PRODUCT_TYPE.DEVICE ||
      item.cartProduct?.productTypeKey === PRODUCT_TYPE.ACCESSORY)
  )
}

const getDeviceItemFromCart = (cartData, deviceSku) => {
  return getAllCartItems(cartData).find(item =>
    item.cartProduct?.sku === deviceSku &&
    (item.cartProduct?.productTypeKey === PRODUCT_TYPE.DEVICE ||
      item.cartProduct?.productTypeKey === PRODUCT_TYPE.ACCESSORY)
  )
}

const getSubscriptionItemFromCart = (cartData) => {
  return getAllCartItems(cartData).find(item =>
    item?.cartProduct?.productTypeKey === PRODUCT_TYPE.MOBILE_SUBSCRIPTION
  )
}

const normalizeMixProducts = (mixProducts) => {
  if (!mixProducts?.length) {
    return ''
  }
  return [...mixProducts].map(String).sort().join(',')
}

const getMixProductsFromSubscription = (subscriptionItem) => {
  const playPoList = subscriptionItem?.itemAttributes?.find(attr => attr.name === 'PlayPoList')?.value
  return playPoList ? normalizeMixProducts(playPoList.split(',')) : ''
}

// eslint-disable-next-line complexity
const subscriptionChannelMatches = (requestedChannel, subscriptionBundleId, subscriptionItem) => {
  if (!requestedChannel && !subscriptionItem) {
    return true
  }
  if (!requestedChannel || !subscriptionItem) {
    return false
  }
  const cartIdentifiers = [
    subscriptionItem.sku,
    subscriptionItem.cartProduct?.sku,
    subscriptionItem.cartProduct?.additionalBillingInformation?.bundleName
  ].filter(Boolean)
  if (subscriptionBundleId && cartIdentifiers.includes(subscriptionBundleId)) {
    return true
  }
  return cartIdentifiers.includes(requestedChannel)
}

// eslint-disable-next-line complexity
const cartMatchesAddRequest = (cartData, { sku, channelKey = '', installmentPeriod = 0, mixProducts = null, subscriptionBundleId = null }) => {
  const device = getDeviceItemFromCart(cartData, sku)
  if (!device) {
    return false
  }
  if ((device.installmentPeriod || 0) !== (installmentPeriod || 0)) {
    return false
  }
  const subscription = getSubscriptionItemFromCart(cartData)
  if (!subscriptionChannelMatches(channelKey, subscriptionBundleId, subscription)) {
    return false
  }
  const requestedMix = normalizeMixProducts(mixProducts)
  const cartMix = getMixProductsFromSubscription(subscription)
  return requestedMix === cartMix
}

export default function useTlmCart (appContext, cart = ref({}), options = {}) {
  const { receiptMode = false } = options
  const hubbleClient = appContext.$hubble;

  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context/hubbleClient to use this composable');
  }
  const cartStore = useCartStore()
  const { resolveFromCartItem, getPdpProductBundlePrice } = useResolveVoucherDevicePrice()
  const {
    cprNumber,
    cvrNumber,
    isProtectedAddress,
    paymentRef,
    actionResponse,
    theCart: storeCart
  } = storeToRefs(cartStore)
  const { removePersistedState } = usePersistedState(appContext)
  const { setScenario, getAttrFromCartScenario, getScenario, deleteCartScenarioCookie } = useCartScenarios(appContext)
  const { completeSetOfCategories } = useCategories(hubbleClient)
  const { isMobileBroadBandSubscription, isSubscription, isMobileVoiceSubscription, isHardware } = useProductType()
  // const { validatePlayMixServicesInCart } = usePlaymix('playmix-4', hubbleClient)
  const { redirectToErrorPage } = useErrorHandler(appContext)
  // Use store cart directly if local cart is empty, otherwise use local cart
  // This ensures reactivity when store is updated from other components
  if (!receiptMode) {
    initializeCart(cart, storeCart)
  }
  const loading = ref(false)
  const notificationAvail = ref(false)
  const notificationMessage = ref('')
  const createCart = async () => {
    notificationAvail.value = false;
    loading.value = true;
    const res = await hubbleClient.cart.createCart();
    actionResponse.value = res.data;
    cart.value = actionResponse.value.responseObject;
    cartStore.setCart(cart.value)
    appContext.$storage.setLocalStorage('tlmCartID', cart.value.id);
    loading.value = false;
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }

  const getActiveCart = () => {
    if (receiptMode && cart.value && Object.keys(cart.value).length > 0) {
      return cart.value
    }
    return getCurrentCart(storeCart, cart)
  }
  const activeCart = computed(() => getActiveCart() || {})

  const cartIsEmpty = () => {
    if (loading.value) {
      return false
    }
    const current = getActiveCart()
    if (!current?.id) {
      return true
    }
    return !!current.cartEmpty
  }

  const persistCategoryIdOnCart = (cartData, itemCategoryId) => {
    if (itemCategoryId && cartData?.cartItemGroupList?.length) {
      setScenario(cartData, 'categoryId', itemCategoryId)
    }
  }

  const persistCartAfterAdd = (loadedCart, actionMessage, itemCategoryId) => {
    cart.value = loadedCart
    cartStore.setCart(cart.value)
    appContext.$storage.setLocalStorage('tlmCartID', cart.value.id)
    notificationAvail.value = true
    notificationMessage.value = actionMessage
    persistCategoryIdOnCart(cart.value, itemCategoryId)
    removePersistedState()
    return cart.value
  }

  const processAddCartItemResponse = (res, itemCategoryId) => {
    if (!res?.data) {
      return false
    }
    actionResponse.value = res.data
    if (actionResponse.value.actionStatus === VALIDATION_STATUS.ERROR) {
      redirectToErrorPage('cartFailure')
      return false
    }
    return persistCartAfterAdd(
      actionResponse.value.responseObject,
      actionResponse.value.actionMessage,
      itemCategoryId
    )
  }

  const ensureActiveCart = async () => {
    const cartId = checkIfCartExist()
    await loadOrCreateCart(cartId || undefined)
    const activeCart = getActiveCart()
    return activeCart?.id ? activeCart : null
  }

  const shouldReuseDeviceCart = (cartData, deviceSku, addRequest) => {
    return !cartIsEmpty() &&
      cartContainsDeviceSku(cartData, deviceSku) &&
      cartMatchesAddRequest(cartData, addRequest)
  }

  const clearCartIfNotEmpty = async () => {
    if (!cartIsEmpty()) {
      await emptyCart()
    }
    return ensureActiveCart()
  }

  const prepareCartForAdd = async (addRequest) => {
    const { sku } = addRequest
    const initialCart = await ensureActiveCart()
    if (!initialCart) {
      return { cart: null, skipAdd: false }
    }
    if (shouldReuseDeviceCart(initialCart, sku, addRequest)) {
      return { cart: initialCart, skipAdd: true }
    }
    const activeCart = await clearCartIfNotEmpty()
    if (!activeCart?.id) {
      return { cart: null, skipAdd: false }
    }
    return { cart: activeCart, skipAdd: false }
  }

  const addCartItem = async ({
    sku,
    channelKey = '',
    installmentPeriod = 0,
    mixProducts = null,
    categoryId = null,
    channelSplit = null,
    partnerAddons = null,
    subscriptionBundleId = null
  }) => {
    const addRequest = { sku, channelKey, installmentPeriod, mixProducts, subscriptionBundleId }
    const { cart: activeCart, skipAdd } = await prepareCartForAdd(addRequest)
    if (!activeCart) {
      return false
    }
    if (skipAdd) {
      persistCategoryIdOnCart(activeCart, categoryId)
      return activeCart
    }
    const cartItem = {
      sku,
      channelKey,
      installmentPeriod,
      mixProducts
    }
    if (partnerAddons?.length) {
      cartItem.partnerAddons = partnerAddons
    }
    const item = {
      cartItems: [cartItem],
      categoryId
    }
    loading.value = true
    const res = await hubbleClient.cart.addCartItem(activeCart.id, item, channelSplit)
      .catch((err) => {
        console.error('error', err)
        redirectToErrorPage('could-not-add-to-cart')
      })
    loading.value = false
    return processAddCartItemResponse(res, categoryId)
  }
  const deleteCart = () => {
    /**
     * commenting it out as we dont intend to delete the cart for now
     * but it might be needed in future
     */
    // loading.value = true;
    // const res = await hubbleClient.cart.deleteCart(cart.value.id);
    // loading.value = false;
    cart.value = {};
    deleteCartCookie()
    deleteCartScenarioCookie()
    cartStore.setCart(cart.value)
    // actionResponse.value = res.data;
    // notificationAvail.value = true;
    // notificationMessage.value = actionResponse.value.actionMessage;
    // return res;
  }
  const deleteCartCookie = () => {
    appContext.$storage.removeLocalStorage('tlmCartID')
    cart.value = {}
    cartStore.setCart(cart.value)
  }


  const checkIfCartExist = () => {
    if (process.client) {
      const storedId = appContext.$storage.getLocalStorage('tlmCartID')
      if (storedId) {
        return storedId
      }
      return undefined
    }
    return getActiveCart()?.id
  }
  const getCartById = async (cartID) => {
    loading.value = true;
    const res = await hubbleClient.cart.getCart(cartID);
    loading.value = false;
    return res;
  }
  const emptyCart = async () => {
    loading.value = true;
    const cartId = checkIfCartExist();
    const res = await hubbleClient.cart.emptyCart(cartId);
    loading.value = false;
    actionResponse.value = res.data;
    if (actionResponse.value.actionStatus === VALIDATION_STATUS.ERROR) {
      cart.value = null
      deleteCartCookie()
      await loadOrCreateCart()
    } else {
      cart.value = actionResponse.value.responseObject;
      cartStore.setCart(cart.value)
      deleteCartScenarioCookie()
      notificationAvail.value = true;
      notificationMessage.value = actionResponse.value.actionMessage;
      return res;
    }
  }
  const deleteCartGroupItem = async (sku) => {
    const cartItemGroupUpdate = {
      cartID: cart.value.id,
      sku
    }
    loading.value = true;
    const res = await hubbleClient.cart.deleteCartGroupItem(cartItemGroupUpdate);
    loading.value = false;
    actionResponse.value = res.data;
    cart.value = actionResponse.value.responseObject;
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }

  const createCartOrder = async (cartID, paymentReference = paymentRef.value) => {
    const resolvedCartID = cartID || getActiveCart()?.id
    if (!resolvedCartID) {
      throw new Error('Cant create order without cartID');
    }

    // CRITICAL: Ensure cart has latest installment prices from store before creating order
    // This ensures receipt/kvittering shows correct voucher-adjusted installment prices
    // Backend reads cart directly from database, so we need to ensure store has latest prices
    const currentCart = getCurrentCart(storeCart, cart);
    if (currentCart) {
      // Update cart with installments to ensure we have correct prices
      const updatedCartWithInstallments = updateCartWithInstallments(currentCart);
      cart.value = updatedCartWithInstallments;
      cartStore.setCart(updatedCartWithInstallments);
      voucherDebug('createCartOrder - Updated cart with installments before order creation:', {
        cartID: resolvedCartID,
        selectedInstallment: currentCart?.cartItemGroupList?.[0]?.cartItems?.[0]?.cartProduct?.selectedPrice?.selectedInstallment
      });
    }

    loading.value = true;
    const res = await hubbleClient.cart.createCartOrder(
      resolvedCartID,
      paymentReference
    );
    loading.value = false;
    actionResponse.value = res.data;
    cart.value = actionResponse.value.responseObject;
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }
  const isSubscriptionItem = (productTypeKey) => {
    return productTypeKey === 'mobile' || productTypeKey === 'subscription'
  }

  const getSubscriptionPriceFromCartItem = (cartItem) => {
    const productTypeKey = cartItem.cartProduct?.productTypeKey
    if (!isSubscriptionItem(productTypeKey)) {
      return null
    }
    return cartItem.cartProduct?.selectedPrice?.lineItemPrice || null
  }

  const findSubscriptionPriceInCartItemGroup = (cartItemGroup) => {
    if (!cartItemGroup?.cartItems) {
      return null
    }
    for (const cartItem of cartItemGroup.cartItems) {
      const subscriptionPrice = getSubscriptionPriceFromCartItem(cartItem)
      if (subscriptionPrice) {
        return subscriptionPrice
      }
    }
    return null
  }

  const findSubscriptionPriceInCart = (cartToUpdate) => {
    if (!cartToUpdate?.cartItemGroupList) {
      return null
    }

    for (const cartItemGroup of cartToUpdate.cartItemGroupList) {
      const subscriptionPrice = findSubscriptionPriceInCartItemGroup(cartItemGroup)
      if (subscriptionPrice) {
        return subscriptionPrice
      }
    }
    return null
  }

  const isValidNumber = (value) => {
    return typeof value === 'number' && !isNaN(value) && value > 0
  }

  const calculateInstallmentAmount = (lineItemPrice, months) => {
    const amount = baseCalculateInstallmentAmount(lineItemPrice, months)
    return amount || 0
  }

  const calculateSubscriptionPriceFromBasePrice = (basePrice, months, monthlyAmountWithSubscription) => {
    if (!basePrice || !monthlyAmountWithSubscription) {
      return null
    }
    const originalInstallmentAmount = basePrice / months
    return monthlyAmountWithSubscription - originalInstallmentAmount
  }

  const calculateMonthlyAmountWithSubscription = (subscriptionPrice, installmentAmount) => {
    if (subscriptionPrice && subscriptionPrice > 0) {
      return subscriptionPrice + installmentAmount
    }
    return installmentAmount
  }

  const getSubscriptionPriceForInstallment = (subscriptionMonthlyPrice, basePrice, selectedInstallment) => {
    if (subscriptionMonthlyPrice) {
      return subscriptionMonthlyPrice
    }
    if (!basePrice || !selectedInstallment.monthlyAmountWithSubscription) {
      return null
    }
    return calculateSubscriptionPriceFromBasePrice(
      basePrice,
      selectedInstallment.months,
      selectedInstallment.monthlyAmountWithSubscription
    )
  }

  const hasValidInstallmentMonths = (selectedInstallment) => {
    return selectedInstallment?.months && isValidNumber(selectedInstallment.months)
  }

  const hasValidLineItemPrice = (lineItemPrice) => {
    return lineItemPrice && isValidNumber(lineItemPrice)
  }

  const canUpdateInstallment = (selectedInstallment, lineItemPrice) => {
    if (!selectedInstallment) {
      return false
    }
    if (!hasValidInstallmentMonths(selectedInstallment)) {
      return false
    }
    return hasValidLineItemPrice(lineItemPrice)
  }

  const extractInstallmentData = (selectedPrice) => {
    if (!selectedPrice) {
      return null
    }
    return {
      selectedInstallment: selectedPrice.selectedInstallment,
      lineItemPrice: selectedPrice.lineItemPrice,
      basePrice: selectedPrice.basePrice
    }
  }

  const calculateInstallmentValues = (lineItemPrice, selectedInstallment, subscriptionMonthlyPrice, basePrice) => {
    const newInstallmentAmount = calculateInstallmentAmount(lineItemPrice, selectedInstallment.months)
    if (newInstallmentAmount === 0) {
      return null
    }

    const calculatedSubscriptionPrice = getSubscriptionPriceForInstallment(
      subscriptionMonthlyPrice,
      basePrice,
      selectedInstallment
    )
    const newMonthlyAmountWithSubscription = calculateMonthlyAmountWithSubscription(
      calculatedSubscriptionPrice,
      newInstallmentAmount
    )

    return {
      amount: newInstallmentAmount,
      monthlyAmountWithSubscription: newMonthlyAmountWithSubscription
    }
  }

  const updateInstallmentValues = (selectedInstallment, values) => {
    if (selectedInstallment && typeof selectedInstallment === 'object' && values) {
      selectedInstallment.amount = values.amount
      selectedInstallment.monthlyAmountWithSubscription = values.monthlyAmountWithSubscription
    }
  }

  // Helper function to check if backend provides monthlyAmountWithSubscription
  const hasBackendProvidedMonthlyAmount = (selectedInstallment) => {
    return selectedInstallment.monthlyAmountWithSubscription && isValidNumber(selectedInstallment.monthlyAmountWithSubscription)
  }

  // Helper function to update installment amount when backend provides monthlyAmountWithSubscription
  const updateInstallmentAmountOnly = (selectedInstallment, lineItemPrice) => {
    const newInstallmentAmount = calculateInstallmentAmount(lineItemPrice, selectedInstallment.months)
    if (newInstallmentAmount > 0) {
      selectedInstallment.amount = newInstallmentAmount
    }
  }

  // Helper function to update installment when backend provides monthlyAmountWithSubscription
  const updateInstallmentWithBackendValue = (selectedInstallment, lineItemPrice) => {
    updateInstallmentAmountOnly(selectedInstallment, lineItemPrice)
    // Keep the backend-provided monthlyAmountWithSubscription
  }

  // Helper function to update installment by recalculating values
  const updateInstallmentByRecalculation = (selectedInstallment, lineItemPrice, subscriptionMonthlyPrice, basePrice) => {
    const values = calculateInstallmentValues(
      lineItemPrice,
      selectedInstallment,
      subscriptionMonthlyPrice,
      basePrice
    )
    updateInstallmentValues(selectedInstallment, values)
  }

  const isValidPriceValue = (value) => {
    return value !== null && value !== undefined && value > 0
  }

  const logRecalculationSuccess = (subscriptionMonthlyPrice, installmentAmount, selectedInstallment, lineItemPrice) => {
    voucherDebug('useTlmCart - Recalculated monthlyAmountWithSubscription:', {
      subscriptionMonthlyPrice,
      installmentAmount,
      total: selectedInstallment.monthlyAmountWithSubscription,
      months: selectedInstallment.months,
      lineItemPrice
    })
  }

  const logRecalculationFailure = (installmentAmount, isValidAmount, subscriptionMonthlyPrice, isValidSubscription, selectedInstallment, lineItemPrice) => {
    voucherDebugWarn('useTlmCart - Cannot recalculate installment:', {
      installmentAmount,
      isValidAmount,
      subscriptionMonthlyPrice,
      isValidSubscription,
      months: selectedInstallment.months,
      lineItemPrice
    })
  }

  const setInstallmentAmountAndTotal = (selectedInstallment, installmentAmount, subscriptionMonthlyPrice) => {
    selectedInstallment.amount = installmentAmount
    selectedInstallment.monthlyAmountWithSubscription = subscriptionMonthlyPrice + installmentAmount
  }

  const recalculateInstallmentFromComponents = (selectedInstallment, lineItemPrice, subscriptionMonthlyPrice) => {
    // Recalculate installment amount from discounted price
    const installmentAmount = calculateInstallmentAmount(lineItemPrice, selectedInstallment.months)
    const isValidAmount = isValidPriceValue(installmentAmount)
    const isValidSubscription = isValidPriceValue(subscriptionMonthlyPrice)

    if (isValidAmount && isValidSubscription) {
      // Always recalculate: monthlyAmountWithSubscription = subscriptionPrice + installmentAmount
      setInstallmentAmountAndTotal(selectedInstallment, installmentAmount, subscriptionMonthlyPrice)
      logRecalculationSuccess(subscriptionMonthlyPrice, installmentAmount, selectedInstallment, lineItemPrice)
      return true
    }
    logRecalculationFailure(installmentAmount, isValidAmount, subscriptionMonthlyPrice, isValidSubscription, selectedInstallment, lineItemPrice)
    return false
  }

  const shouldRecalculateInstallment = (subscriptionMonthlyPrice, selectedInstallment) => {
    return subscriptionMonthlyPrice !== null && selectedInstallment.months > 0
  }

  const updateInstallmentWithRecalculation = (selectedInstallment, lineItemPrice, subscriptionMonthlyPrice, basePrice) => {
    // CRITICAL: Always recalculate monthlyAmountWithSubscription as subscriptionPrice + installmentAmount
    // This ensures correctness even if backend provides an incorrect value (e.g., 558 instead of 533)
    // The backend might calculate it incorrectly, so we always recalculate to ensure: monthlyAmountWithSubscription = subscriptionPrice + installmentAmount
    if (shouldRecalculateInstallment(subscriptionMonthlyPrice, selectedInstallment)) {
      const recalculated = recalculateInstallmentFromComponents(selectedInstallment, lineItemPrice, subscriptionMonthlyPrice)
      if (recalculated) {
        return
      }
      // If recalculation failed, log warning but continue to fallback
      voucherDebugWarn('useTlmCart - Recalculation failed, using fallback:', {
        subscriptionMonthlyPrice,
        lineItemPrice,
        months: selectedInstallment.months,
        backendValue: selectedInstallment.monthlyAmountWithSubscription
      })
    }

    // Fallback: If backend already provides monthlyAmountWithSubscription and we can't recalculate,
    // use it directly (but this should rarely happen)
    if (hasBackendProvidedMonthlyAmount(selectedInstallment)) {
      voucherDebugWarn('useTlmCart - Using backend-provided monthlyAmountWithSubscription (may be incorrect):', {
        backendValue: selectedInstallment.monthlyAmountWithSubscription,
        subscriptionMonthlyPrice,
        lineItemPrice,
        months: selectedInstallment.months
      })
      updateInstallmentWithBackendValue(selectedInstallment, lineItemPrice)
      return
    }

    // Otherwise, calculate both values
    updateInstallmentByRecalculation(selectedInstallment, lineItemPrice, subscriptionMonthlyPrice, basePrice)
  }

  const getSubscriptionPriceFromInstallmentData = (selectedInstallment, installmentAmount) => {
    // If we have monthlyAmountWithSubscription and installmentAmount, we can calculate subscription price
    if (selectedInstallment?.monthlyAmountWithSubscription && installmentAmount) {
      const calculatedSubscription = selectedInstallment.monthlyAmountWithSubscription - installmentAmount
      if (calculatedSubscription > 0) {
        return calculatedSubscription
      }
    }
    return null
  }

  const extractFinalSubscriptionPrice = (subscriptionMonthlyPrice, selectedInstallment, lineItemPrice) => {
    // If subscriptionMonthlyPrice is null, try to extract it from the installment data
    if (subscriptionMonthlyPrice) {
      return subscriptionMonthlyPrice
    }
    if (!selectedInstallment) {
      return null
    }
    const calculatedInstallmentAmount = calculateInstallmentAmount(lineItemPrice, selectedInstallment.months)
    return getSubscriptionPriceFromInstallmentData(selectedInstallment, calculatedInstallmentAmount)
  }

  const logBeforeUpdate = (selectedInstallment, subscriptionMonthlyPrice, finalSubscriptionPrice, lineItemPrice, basePrice) => {
    voucherDebug('useTlmCart - updateSingleCartItemInstallment:', {
      before: {
        amount: selectedInstallment.amount,
        monthlyAmountWithSubscription: selectedInstallment.monthlyAmountWithSubscription,
        months: selectedInstallment.months
      },
      subscriptionMonthlyPrice,
      finalSubscriptionPrice,
      lineItemPrice,
      basePrice
    })
  }

  const logAfterUpdate = (selectedInstallment) => {
    voucherDebug('useTlmCart - updateSingleCartItemInstallment - after:', {
      amount: selectedInstallment.amount,
      monthlyAmountWithSubscription: selectedInstallment.monthlyAmountWithSubscription,
      months: selectedInstallment.months
    })
  }

  const updateSingleCartItemInstallment = (cartItem, subscriptionMonthlyPrice, cartToUpdate) => {
    try {
      const selectedPrice = cartItem?.cartProduct?.selectedPrice
      const installmentData = extractInstallmentData(selectedPrice)
      if (!installmentData) {
        return
      }

      const { selectedInstallment, basePrice } = installmentData
      const lineItemPrice = resolveFromCartItem(cartItem, cartToUpdate)
      if (!canUpdateInstallment(selectedInstallment, lineItemPrice)) {
        return
      }

      const finalSubscriptionPrice = extractFinalSubscriptionPrice(subscriptionMonthlyPrice, selectedInstallment, lineItemPrice)
      logBeforeUpdate(selectedInstallment, subscriptionMonthlyPrice, finalSubscriptionPrice, lineItemPrice, basePrice)
      updateInstallmentWithRecalculation(selectedInstallment, lineItemPrice, finalSubscriptionPrice, basePrice)
      logAfterUpdate(selectedInstallment)
    } catch (error) {
      console.error('Error updating cart item installment:', error, cartItem)
    }
  }

  // CRITICAL: This function updates cart's selectedInstallment with correct prices
  // It's needed because:
  // 1. Checkout/receipt pages read cart.selectedInstallment directly from cart object
  // 2. Backend reads cart directly from database before creating order (receipt/kvittering)
  // 3. Cart loads from API may not have correct installment prices calculated
  // 4. MitID callback and other cart updates need price recalculation
  //
  // Note: VoucherCode.vue also updates cart.selectedInstallment when voucher is applied/removed,
  // but this function handles cases where cart is loaded/updated outside of voucher flow.
  const updateInstallmentAmountsFromLineItemPrice = (cartToUpdate) => {
    if (!cartToUpdate || !cartToUpdate.cartItemGroupList || !Array.isArray(cartToUpdate.cartItemGroupList)) {
      return cartToUpdate
    }

    try {
      cartToUpdate.cartItemGroupList.forEach(cartItemGroup => {
        if (!cartItemGroup || !Array.isArray(cartItemGroup.cartItems)) {
          return
        }
        // Find subscription price for this specific cart item group
        // This ensures we get the correct subscription price even if there are multiple groups
        const subscriptionMonthlyPrice = findSubscriptionPriceInCartItemGroup(cartItemGroup)
        // If not found in this group, try finding it in the entire cart as fallback
        const finalSubscriptionPrice = subscriptionMonthlyPrice || findSubscriptionPriceInCart(cartToUpdate)

        cartItemGroup.cartItems.forEach(cartItem => {
          if (cartItem && cartItem.cartProduct) {
            updateSingleCartItemInstallment(cartItem, finalSubscriptionPrice, cartToUpdate)
          }
        })
      })
    } catch (error) {
      console.error('Error updating installment amounts from line item price:', error, cartToUpdate)
    }

    return cartToUpdate
  }

  const getCartId = (id) => {
    if (typeof id !== 'undefined' && id !== null) {
      return id
    }
    return checkIfCartExist()
  }

  const validateCartResponse = (loadedCart) => {
    if (!loadedCart) {
      console.error('Cart response object is missing')
      return false
    }
    return true
  }

  // Wrapper function to safely update cart with installment prices
  // Used in: loadOrCreateCart, updateCart, createCartOrder
  const updateCartWithInstallments = (loadedCart) => {
    try {
      const cartWithDevicePrices = patchDeviceLineItemPricesInCart(loadedCart, {
        resolveFromCartItem,
        getPdpProductBundlePrice
      })
      return updateInstallmentAmountsFromLineItemPrice(cartWithDevicePrices)
    } catch (error) {
      console.error('Error updating installment amounts, continuing with original cart:', error)
      return loadedCart
    }
  }

  const saveCartToStore = (loadedCart, actionResponseData) => {
    cart.value = loadedCart
    cartStore.setCart(cart.value)
    appContext.$storage.setLocalStorage('tlmCartID', cart.value.id)
    notificationAvail.value = true
    notificationMessage.value = actionResponseData.actionMessage
    getScenario()
  }

  const loadOrCreateCart = async (id) => {
    loading.value = true
    try {
      const cartID = getCartId(id)
      const res = await hubbleClient.cart.loadOrCreateCart(cartID)
      loading.value = false
      actionResponse.value = res.data
      let loadedCart = actionResponse.value.responseObject

      if (!validateCartResponse(loadedCart)) {
        loading.value = false
        return res
      }

      loadedCart = updateCartWithInstallments(loadedCart)
      saveCartToStore(loadedCart, actionResponse.value)
      return res
    } catch (error) {
      loading.value = false
      console.error('Error loading or creating cart:', error)
      throw error
    }
  }

  const getCategoryForCartItemGroup = () => {
    const id = getAttrFromCartScenario(getActiveCart(), 'categoryId')
    return id
  }

  const isCartEmpty = computed(() => cartIsEmpty())
  const skuListForStockCheck = computed(() => {
    const skuList = []
    activeCart.value.cartItemGroupList?.forEach(group => {
      group.cartItems.forEach(cartItem => {
        if (cartItem.cartProduct.productTypeKey !== PRODUCT_TYPE.MOBILE_SUBSCRIPTION) {
          skuList.push(cartItem.sku)
        }
      })
    })
    return skuList
  })

  const checkCartItems = (updatedCart) => {
    const currentCartItems = cart.value?.cartItemGroupList
    let output = true
    // check if the items in cart has changed NLOS-2472
    try {
      const newCart = updatedCart
      currentCartItems.forEach((item, i) => {
        const newItem = newCart.cartItemGroupList[i]
        item.cartItems.forEach((cartItem, j) => {
          const newCartItem = newItem.cartItems[j]
          if (newCartItem.sku !== cartItem.sku) {
            $nuxt.$emit('cartItemsChanged')
            output = false
          }
        })
      })
    } catch (error) {
      console.error(error);
    }
    return output
  }


  const updateCart = (updatedCart) => {
    if (updatedCart) {
      checkCartItems(updatedCart)
      // Update installment amounts from cart to ensure prices are preserved (especially after voucher is applied or mitID callback)
      const updatedCartWithInstallments = updateCartWithInstallments(updatedCart)
      cart.value = updatedCartWithInstallments;
      cartStore.setCart(cart.value)
    }
  }

  const deviceList = computed(() => {
    const deviceItems = []
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (device !== undefined) {
        return deviceItems.push(device)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return deviceItems[0]
  })
  const subscriptionList = computed(() => {
    const carteItemsWiThSubscription = []
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const subscription = cartItemGroup.cartItems?.find(
        cartItem => cartItem?.cartProduct && isSubscription(cartItem.cartProduct.productTypeKey)
      )
      if (subscription !== undefined) {
        return carteItemsWiThSubscription.push(subscription)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return carteItemsWiThSubscription[0]
  })
  const installments = computed(() => {
    const installments = []
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (device !== undefined) {
        return installments.push(device.installmentPeriod)
      }
    })
    // when BE can handle multiple installments, we will send it as an array of values
    return installments.length > 0 ? installments[0] : 0
  })

  const isSubscriptionOnly = computed(() => { // (sim-only)
    let subscriptionOnly = false
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const notSubscription = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (!notSubscription) {
        subscriptionOnly = true
      }
    })
    return subscriptionOnly
  })

  const cartHasSubscription = computed(() => {
    return activeCart.value?.cartItemGroupList?.some(cartItemGroup =>
      cartItemGroup.cartItems.some(cartItem =>
        isSubscription(cartItem.cartProduct.productTypeKey)
      )
    ) ?? false
  })

  // MIO-29627: any device/accessory/MBB hardware blocks Erhvervskunde checkout
  const cartHasHardware = computed(() => {
    return activeCart.value?.cartItemGroupList?.some(cartItemGroup =>
      cartItemGroup.cartItems?.some(cartItem =>
        isHardware(cartItem?.cartProduct?.productTypeKey)
      )
    ) ?? false
  })
  const isMobileVoiceSubscriptionOnly = computed(() => { // ( Mobile voice sim-only)
    let mvSubscriptionOnly = false
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const notASubscription = cartItemGroup.cartItems.find(cartItem => !isMobileVoiceSubscription(cartItem.cartProduct.productTypeKey))
      if (!notASubscription) {
        mvSubscriptionOnly = true
      }
    })
    return mvSubscriptionOnly
  })


  const hasInstallment = (cartItem) => {
    return cartItem.installmentPeriod > 0
  }
  const getItemAttributeFromCartItem = (cartItem, itemAttribute) => {
    return cartItem?.itemAttributes?.find(attr => attr.name === itemAttribute) !== undefined
      ? cartItem?.itemAttributes?.find(attr => attr.name === itemAttribute).value
      : null
  }
  const mixPoList = computed(() => {
    const mixPoList = getItemAttributeFromCartItem(subscriptionList.value, 'PlayPoList')
    return mixPoList !== null ? mixPoList.split(',').map(val => val) : null
  })
  const partnerAddonsList = computed(() => {
    const raw = getItemAttributeFromCartItem(subscriptionList.value, 'PartnerAddons')
    if (raw == null || raw === '') {
      return null
    }
    return raw.split(',').map(val => val.trim()).filter(Boolean)
  })
  const isNumberPorting = (cartItem) => {
    return getItemAttributeFromCartItem(cartItem, 'IsNumberPorting') === 'true'
  }
  const hasPhoneNumber = (cartItem) => {
    return getItemAttributeFromCartItem(cartItem, 'PhoneNumber') !== null
  }
  const isPreOrder = computed(() => {
    let isPreOrder = false
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThPurchaseCategory = cartItemGroup.cartItems.find(cartItem => {
        const purchaseCategory = getItemAttributeFromCartItem(cartItem, 'PurchaseCategory')
        return PURCHASE_CATEGORY.PRE_ORDER === purchaseCategory
      })
      if (carteItemWiThPurchaseCategory !== undefined) {
        isPreOrder = true;
      }
    })
    return isPreOrder;
  })

  const getItemAttributesForSubscriptionFromCart = (attrName) => {
    let attrVal = null
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const subscriptionInCart = cartItemGroup.cartItems.find(cartItem => isSubscription(cartItem.cartProduct.productTypeKey))
      attrVal = subscriptionInCart !== undefined ? getItemAttributeFromCartItem(subscriptionInCart, attrName) : null
    })
    return attrVal
  }
  const isNewNumber = (cartItem) => {
    return !isNumberPorting(cartItem) && hasPhoneNumber(cartItem)
  }
  const isNewNumberSelected = (cartItem, subscription) => {
    return isNewNumber(cartItem) || subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER
  }
  const isNumberPortingSelected = (cartItem, subscription) => {
    return isNumberPorting(cartItem) || subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER
  }
  const isChangeSubscription = (subscription) => {
    return subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION
  }
  const isKeepSubscription = (subscription) => {
    return subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.KEEP_SUBSCRIPTION
  }
  const isAnyItemInPreOrderOrBackOrder = computed(() => {
    let isAnyItemInPreOrderOrBackOrder = false
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThPurchaseCategory = cartItemGroup.cartItems.find(cartItem => {
        const purchaseCategory = getItemAttributeFromCartItem(cartItem, 'PurchaseCategory')
        return PURCHASE_CATEGORY.PRE_ORDER === purchaseCategory || PURCHASE_CATEGORY.BACK_ORDER === purchaseCategory
      })
      if (carteItemWiThPurchaseCategory !== undefined) {
        isAnyItemInPreOrderOrBackOrder = true;
      }
    })
    return isAnyItemInPreOrderOrBackOrder;
  })

  const isAnyItemInPreOrder = computed(() => {
    let available = false
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThPurchaseCategory = cartItemGroup.cartItems.find(cartItem => {
        const purchaseCategory = getItemAttributeFromCartItem(cartItem, 'PurchaseCategory')
        return PURCHASE_CATEGORY.PRE_ORDER === purchaseCategory
      })
      if (carteItemWiThPurchaseCategory !== undefined) {
        available = true;
      }
    })
    return available;
  })

  const isAnyItemInBackOrder = computed(() => {
    let available = false
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThPurchaseCategory = cartItemGroup.cartItems.find(cartItem => {
        const purchaseCategory = getItemAttributeFromCartItem(cartItem, 'PurchaseCategory')
        return PURCHASE_CATEGORY.BACK_ORDER === purchaseCategory
      })
      if (carteItemWiThPurchaseCategory !== undefined) {
        available = true;
      }
    })
    return available;
  })

  const isEsimProduct = product => {
    return (product.productAttributes.find(attr => {
      return attr.name === 'E-SIM' ? (attr.value === 'true') : false
    })?.value === 'true')
  }

  const hasAnyEsim = () => {
    let anyEsim = false
    activeCart.value?.cartItemGroupList?.forEach(cartItemGroup =>
      cartItemGroup.cartItems.find(cartItem => {
        if (isEsimProduct(cartItem.cartProduct)) {
          anyEsim = true
          return true
        }
        return false
      }
      ));

    return anyEsim
  }

  const isMobiltelefonerCategory = computed(() => {
    const categoryId = getCategoryForCartItemGroup()
    const categorySlug = completeSetOfCategories?.value?.find(category => category.id === categoryId)?.slug?.toLowerCase()
    return categorySlug === 'mobiltelefoner'
  })

  const isEsimOnlyDevice = computed(() => {
    if (!isMobiltelefonerCategory.value) {
      return false
    }

    const isEsimOnlyDevice = activeCart.value?.cartItemGroupList?.some(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(
        cartItem => !isSubscription(cartItem.cartProduct.productTypeKey)
      )

      return device?.cartProduct?.productAttributes?.some(
        attr => attr.name === 'PhysicalSIM' && attr.value === 'false'
      )
    })

    return isEsimOnlyDevice
  })

  const paymentOptions = computed(() => {
    return activeCart.value.paymentOptions
  })
  const isPayGSubscription = (bundleType) => {
    return bundleType === BUNDLE_TYPE.PAYG
  }
  const billingAddressFromCart = computed(() => {
    return activeCart.value.billingAddress
  })
  const containsKeepSubscription = computed(() => activeCart.value?.keepSubscriptionType)
  const customerOutOfDiscountedPeriod = computed(() => activeCart.value?.customerOutOfDiscountedPeriod)
  return {
    isSubscriptionOnly,
    cartHasSubscription,
    cartHasHardware,
    hasPhoneNumber,
    billingAddressFromCart,
    getItemAttributesForSubscriptionFromCart,
    isMobileVoiceSubscriptionOnly,
    isPayGSubscription,
    getScenario,
    paymentOptions,
    isMobileBroadBandSubscription,
    hasAnyEsim,
    getCategoryForCartItemGroup,
    persistCategoryIdOnCart,
    isAnyItemInPreOrderOrBackOrder,
    checkCartItems,
    cprNumber,
    cvrNumber,
    isProtectedAddress,
    isNewNumber,
    isChangeSubscription,
    isNumberPortingSelected,
    isNewNumberSelected,
    isNumberPorting,
    getItemAttributeFromCartItem,
    cart: activeCart,
    getCartID: computed(() => activeCart.value?.id),
    cartID: computed(() => activeCart.value?.id),
    isLoading: computed(() => loading.value),
    notificationAvailable: computed(() => notificationAvail.value),
    notificationMessage: computed(() => notificationMessage.value),
    actionResponse: computed(() => actionResponse.value),
    cartLoaded: computed(() => activeCart.value && Object.keys(activeCart.value).length > 0),
    containsKeepSubscription,
    customerOutOfDiscountedPeriod,
    updateCart,
    notificationRead: () => { notificationAvail.value = false },
    addCartItem,
    createCart,
    deleteCart,
    deleteCartCookie,
    deleteCartGroupItem,
    emptyCart,
    createCartOrder,
    loadOrCreateCart,
    isCartEmpty,
    skuListForStockCheck,
    subscriptionList,
    mixPoList,
    partnerAddonsList,
    installments,
    isSubscription,
    hasInstallment,
    deviceList,
    setPaymentRef: (payRef) => { paymentRef.value = payRef; },
    getCartById,
    checkIfCartExist,
    isKeepSubscription,
    keepSubCustInDiscountedPeriod: computed(() => containsKeepSubscription.value ? !customerOutOfDiscountedPeriod.value : true),
    isPreOrder,
    isMobiltelefonerCategory,
    isEsimOnlyDevice,
    isAnyItemInPreOrder,
    isAnyItemInBackOrder
  }
}
